import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Type, Stack, Modal, Icon } from 'react-spark';
import { formatMoney, buildIncrementedList } from '../../../../utils/numbers';
import { FONT_SIZES } from '../../../../utils/novaTypography';
import { AmountTicker } from '../../amount-ticker';
import actionCreator from '../../redux/customize-offers/action-creator';

class OffersV3CustomizeComponent extends Component {
    static propTypes = {
        clientStateMinAmount: PropTypes.number.isRequired,
        customizedOffer: PropTypes.number,
        maxOffer: PropTypes.number.isRequired,
        minOffer: PropTypes.number.isRequired,
        submit: PropTypes.func.isRequired,
        isModalOpen: PropTypes.bool,
        onCloseModal: PropTypes.func.isRequired,
        requestedAmountAvailable: PropTypes.bool,
    };

    static defaultProps = {
        isModalOpen: false,
        requestedAmountAvailable: false,
    };

    constructor(props) {
        super(props);
        this.state = {
            isMobile: false,
            currentTickerValue: props.customizedOffer || props.maxOffer,
            wasModalOpen: props.isModalOpen,
            lastCustomizedOffer: props.customizedOffer
        };
    }

    componentDidMount() {
        this.handleResize();
        window.addEventListener('resize', this.handleResize);
    }

    componentDidUpdate(prevProps, prevState) {
        const { isModalOpen } = this.props;
        const { isMobile } = this.state;
        
        const modalStateChanged = prevProps.isModalOpen !== isModalOpen;
        const mobileStateChanged = prevState.isMobile !== isMobile;
        
        if (modalStateChanged || mobileStateChanged) {
            // Only prevent body scrolling for mobile bottom sheet
            if (isMobile && isModalOpen) {
                document.body.classList.add('bottom-sheet-open');
            } else {
                document.body.classList.remove('bottom-sheet-open');
            }
        }
        
        // Handle modal mask ID for FullStory tracking (desktop)
        if (modalStateChanged) {
            if (isModalOpen && !prevProps.isModalOpen) {
                // Modal just opened - add ID to background mask
                const modalMask = document.querySelector('.sprk-c-ModalMask');
                if (modalMask) {
                    modalMask.id = "close-ticker-overlay";
                }
            } else if (!isModalOpen && prevProps.isModalOpen) {
                const modalMask = document.querySelector('.sprk-c-ModalMask');
                if (modalMask) {
                    modalMask.removeAttribute('id');
                }
            }
        }
    }

    componentWillUnmount() {
        window.removeEventListener('resize', this.handleResize);
        document.body.classList.remove('bottom-sheet-open');
    }

    static getDerivedStateFromProps(nextProps, prevState) {
        // Reset currentTickerValue when modal opens or when customizedOffer changes
        const shouldReset = (
            (nextProps.isModalOpen && !prevState.wasModalOpen) ||
            (nextProps.customizedOffer !== prevState.lastCustomizedOffer)
        );

        if (shouldReset) {
            return {
                currentTickerValue: nextProps.customizedOffer || nextProps.maxOffer,
                wasModalOpen: nextProps.isModalOpen,
                lastCustomizedOffer: nextProps.customizedOffer
            };
        }

        // Track modal state for next comparison
        if (nextProps.isModalOpen !== prevState.wasModalOpen) {
            return {
                wasModalOpen: nextProps.isModalOpen,
                lastCustomizedOffer: nextProps.customizedOffer
            };
        }

        return null;
    }

    handleResize = () => {
        this.setState({
            isMobile: window.innerWidth < 550
        });
    };

    handleTickerChange = (value) => {
        this.setState({ currentTickerValue: value });
    };

    handleSave = () => {
        const { currentTickerValue } = this.state;
        this.props.submit(currentTickerValue);
        this.props.onCloseModal();
        
        // Scroll to top while new offers are fetched
        setTimeout(() => {
            window.scrollTo({
                top: 0,
                behavior: 'smooth',
            });
        });
    };

    generateOptions = () => {
        const { maxOffer } = this.props;
        return buildIncrementedList(
            this.lowestLoanAmount,
            maxOffer, 
            500
        );
    };

    get lowestLoanAmount() {
        const { clientStateMinAmount, minOffer } = this.props;
        return Math.max(minOffer || 0, clientStateMinAmount);
    }

    render() {
        const { isModalOpen, onCloseModal } = this.props;
        const { isMobile, currentTickerValue } = this.state;
        const options = this.generateOptions();

        if (!isModalOpen) {
            return null;
        }

        return (
            <Fragment>
                {/* Desktop Modal */}
                {!isMobile && (
                    <Modal
                        id="offersV3AdjustAmountModal"
                        type="info"
                        show={isModalOpen}
                        onClose={onCloseModal}
                        title={"How much would you like to borrow?"}
                    >
                        {/* Custom close button */}
                        <button
                            id="close-ticker"
                            className="offers-v3-custom-close-button"
                            type="button"
                            aria-label="Close Modal"
                            onClick={onCloseModal}
                        >
                            <Icon name='close' style={{ height: 36, width: 36, strokeWidth: 2 }} color='base' />
                        </button>
                        <Stack>
                            <Stack.Item>
                                <Type.DisplayFour className="sprk-u-pbm sprk-u-mvn sprk-u-TextAlign--center" style={{ fontSize: FONT_SIZES.display }}>
                                    {formatMoney(currentTickerValue)}
                                </Type.DisplayFour>
                                <div style={{ maxWidth: '350px', margin: '0 auto' }}>
                                    <AmountTicker
                                        options={options}
                                        value={currentTickerValue}
                                        onChange={this.handleTickerChange}
                                    />
                                </div>
                            </Stack.Item>
                            <Stack.Item className="sprk-u-TextAlign--right">
                                <button 
                                    id="save-amount-ticker"
                                    className="offers-v3-save-button"
                                    onClick={this.handleSave}
                                >
                                    Save
                                </button>
                            </Stack.Item>
                        </Stack>
                    </Modal>
                )}

                {/* Mobile Bottom Sheet */}
                {isMobile && (
                    <Fragment>
                        <div className="bottom-sheet-overlay" id="close-ticker-overlay" onClick={onCloseModal} />
                        <div className="bottom-sheet">
                            <div className="bottom-sheet__header">
                                <Type.BodyTwo className="bottom-sheet__title">
                                    How much would you like to borrow?
                                </Type.BodyTwo>
                                <button
                                    id="close-ticker"
                                    className="bottom-sheet__close"
                                    onClick={onCloseModal}
                                    aria-label="Close"
                                >
                                    <Icon name='close' style={{ height: 48, width: 48, strokeWidth: 2 }} color='base' />
                                </button>
                            </div>
                            <div className="bottom-sheet__content">
                                {/* Dollar Amount Display */}
                                <Type.DisplayFour className="sprk-u-pbm sprk-u-mvn sprk-u-TextAlign--center" style={{ fontSize: FONT_SIZES.hero }}>
                                    {formatMoney(currentTickerValue)}
                                </Type.DisplayFour>
                                <div style={{ maxWidth: '322px', margin: '0 auto' }}>
                                    <AmountTicker
                                        options={options}
                                        value={currentTickerValue}
                                        onChange={this.handleTickerChange}
                                    />
                                </div>
                                <div className="sprk-u-TextAlign--center sprk-u-ptm">
                                    <button 
                                        className="offers-v3-save-button"
                                        onClick={this.handleSave}
                                        id="save-amount-ticker"
                                    >
                                        Save
                                    </button>
                                </div>
                            </div>
                        </div>
                    </Fragment>
                )}
            </Fragment>
        );
    }
}

const mapDispatchToProps = (dispatch) => ({
    submit: (requestedAmount) => dispatch((_, getState) => {
        const state = getState();
        if (state && state.loan && state.loan.loanJson && state.loan.loanJson.loanId) {
            dispatch(actionCreator.submitAuthenticated(requestedAmount, state.loan.loanJson.loanId));
        } else {
            dispatch(actionCreator.submit(requestedAmount));
        }
    }),
});

export const OffersV3Customize = connect(
    null,
    mapDispatchToProps
)(OffersV3CustomizeComponent);
